import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthTokenService } from '@core/auth/auth-token.service';
import { API_URL } from '@core/config/api-url.token';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { AppRoutes } from '@shared/constants/routes.enum';
import { Observable, catchError, finalize, from, map, shareReplay, switchMap, throwError } from 'rxjs';
import type { TokenResponseDto } from '../dtos/auth.dto';
import { AuthMapper } from '../mappers/auth.mapper';

@Injectable()
export class AuthRefreshService {
  private readonly tokenService = inject(AuthTokenService);
  private readonly mapper = inject(AuthMapper);
  private readonly backend = inject(HttpBackend);
  private readonly apiUrl = inject(API_URL);
  private readonly router = inject(Router);

  private pending$: Observable<string> | null = null;

  public refresh(): Observable<string> {
    const sessionTokens = this.tokenService.tokens();
    const refreshToken = sessionTokens?.refreshToken;
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    this.pending$ ??= new HttpClient(this.backend)
      .post<TokenResponseDto>(`${this.apiUrl}${API_ENDPOINTS.auth.refresh}`, { RefreshToken: refreshToken })
      .pipe(
        switchMap(dto => {
          if (this.tokenService.tokens() !== sessionTokens) return throwError(() => new Error('Session changed'));
          const tokens = this.mapper.toTokens(dto);
          return from(this.tokenService.save(tokens)).pipe(map(() => tokens.accessToken));
        }),
        catchError(err => {
          if (this.tokenService.tokens() !== sessionTokens) return throwError(() => err);
          return from(this.tokenService.clear()).pipe(
            switchMap(() => {
              void this.router.navigate([AppRoutes.Login]);
              return throwError(() => err);
            }),
          );
        }),
        finalize(() => (this.pending$ = null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.pending$;
  }
}
